import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useAuth } from '@/contexts/AuthContext';
import { notificationAPI } from '@/lib/api';
import { NOTIFICATIONS_READ_EVENT } from '@/hooks/useNotificationUnread';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Bell, BellOff, CheckCircle, Info, AlertTriangle, Loader2, HelpCircle, MessageCircle, Receipt, Users, CreditCard } from 'lucide-react';

// The backend writes `notification_type`; older code read `type`. Keep
// both for safety and resolve to one name here (PR 7).
function _typeOf(n) {
  return n?.notification_type || n?.type || 'info';
}

const typeIcons = {
  info: Info,
  success: CheckCircle,
  warning: AlertTriangle,
  order: Bell,
  invitation: Bell,
  relationship_invite: Users,
  relationship_accepted: Users,
  relationship_rejected: Users,
  product_request: HelpCircle,
  chat_message: MessageCircle,
  payment: Receipt,
  plan_renewal: CreditCard,
};

const typeColors = {
  info: 'text-farm-info bg-farm-info/10',
  success: 'text-farm-success bg-farm-success/10',
  warning: 'text-farm-warning bg-farm-warning/10',
  order: 'text-farm-primary bg-farm-primary/10',
  invitation: 'text-farm-secondary bg-farm-secondary/10',
  relationship_invite: 'text-farm-secondary bg-farm-secondary/10',
  relationship_accepted: 'text-farm-success bg-farm-success/10',
  relationship_rejected: 'text-farm-warning bg-farm-warning/10',
  product_request: 'text-farm-primary bg-farm-primary/10',
  chat_message: 'text-farm-primary bg-farm-primary/10',
  payment: 'text-farm-success bg-farm-success/10',
  plan_renewal: 'text-farm-warning bg-farm-warning/10',
};

// Both point at a chat (related_id is the conversation id).
// Chats between users were removed (PR 56): old chat and product-request
// notifications stay readable but no longer open anything.
const OPENS_CHAT = new Set();
// Farm produce chats (PR 67) open that chat; related_id is the chat id.
const PRODUCE_CHAT = 'produce_chat';
// The support team made this farmer a cluster manager: opens My cluster.
const CLUSTER_MANAGER = 'cluster_manager';
// A payment went through or failed: opens Payments & receipts.
const PAYMENT = 'payment';
// A paid plan ends in 3 days, or has ended: opens the plans page to pay.
const PLAN_RENEWAL = 'plan_renewal';

// Chat notifications are rendered from their fields so they read in
// the user's language and show how many messages are waiting.
// Plan and payment notices carry their facts, so they read in the user's
// language too; older ones without them show the stored English text.
function notificationText(n, type, t, lang) {
  const day = (iso) => (iso
    ? new Date(iso).toLocaleDateString(lang, { day: 'numeric', month: 'short', year: 'numeric' })
    : '');
  if (type === PLAN_RENEWAL && n.renewal) {
    return {
      title: t(`notifications.plan.${n.renewal}.title`, n.title),
      message: t(`notifications.plan.${n.renewal}.message`,
        { plan: n.plan_name || '', date: day(n.ends_on), defaultValue: n.message }),
    };
  }
  if (type === PAYMENT && n.outcome) {
    return {
      title: t(`notifications.payment.${n.outcome}.title`, n.title),
      message: t(`notifications.payment.${n.outcome}.message`,
        { amount: n.amount || '', plan: n.plan || '', date: day(n.paid_up_to), defaultValue: n.message }),
    };
  }
  if (type !== 'chat_message') return { title: n.title, message: n.message };
  const count = Number(n.count) || 1;
  return {
    title: t('notifications.chat.title', { count, name: n.sender_name || '' }),
    message: n.preview_kind === 'photo' ? t('chat.preview.photo') : n.preview,
  };
}

export default function NotificationsPage() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const { t, i18n } = useTranslation();
  const [notifications, setNotifications] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    loadNotifications();
  }, [user.id]);

  const loadNotifications = async () => {
    try {
      const res = await notificationAPI.get(user.id);
      setNotifications(res.data || []);
    } catch (error) {
      console.error('Failed to load notifications', error);
    } finally {
      setLoading(false);
    }
  };

  const markAsRead = async (notificationId) => {
    try {
      await notificationAPI.markRead(notificationId);
      setNotifications((prev) =>
        prev.map((n) => (n.id === notificationId ? { ...n, read: true } : n))
      );
      window.dispatchEvent(new Event(NOTIFICATIONS_READ_EVENT));
    } catch (error) {
      console.error('Failed to mark as read', error);
    }
  };

  if (loading) {
    return <div className="flex items-center justify-center h-64"><Loader2 className="w-8 h-8 animate-spin text-farm-primary" /></div>;
  }

  return (
    <div className="p-4 md:p-6 lg:p-8 animate-fadeIn">
      <div className="mb-6">
        <h1 className="font-heading text-2xl md:text-3xl font-bold">{t('notifications.title')}</h1>
        <p className="text-muted-foreground">{t('notifications.subtitle')}</p>
      </div>

      {notifications.length === 0 ? (
        <Card>
          <CardContent className="flex flex-col items-center justify-center py-16">
            <BellOff className="w-16 h-16 text-muted-foreground mb-4" />
            <h3 className="font-heading text-xl font-semibold mb-2">{t('notifications.empty.title')}</h3>
            <p className="text-muted-foreground">{t('notifications.empty.subtitle')}</p>
          </CardContent>
        </Card>
      ) : (
        <div className="space-y-3">
          {notifications.map((notification) => {
            const type = _typeOf(notification);
            const Icon = typeIcons[type] || Bell;
            const colorClass = typeColors[type] || 'text-muted-foreground bg-muted';
            const isUnread = !notification.read && !notification.is_read;
            // Chat-linked notifications open that chat (a product request
            // is the product card there); opening it marks them read.
            const relatedId = notification.related_id;
            const opensChat = ((OPENS_CHAT.has(type) || type === PRODUCE_CHAT) && !!relatedId)
              || type === CLUSTER_MANAGER || type === PAYMENT || type === PLAN_RENEWAL;
            const openRelated = () => {
              if (type === CLUSTER_MANAGER) navigate('/cluster');
              else if (type === PAYMENT) navigate('/payments');
              else if (type === PLAN_RENEWAL) navigate('/subscription/plans');
              else if (type === PRODUCE_CHAT && relatedId) navigate(`/produce/chats/${relatedId}`);
              else if (opensChat) navigate('/support/chat');
            };
            const { title, message } = notificationText(notification, type, t, i18n.language);
            return (
              <Card
                key={notification.id}
                className={`transition-all ${isUnread ? 'border-l-4 border-l-farm-primary' : 'opacity-60'} ${opensChat ? 'cursor-pointer hover:shadow-md' : ''}`}
                onClick={openRelated}
                data-testid={`notification-${type}`}
              >
                <CardContent className="p-4">
                  <div className="flex items-start gap-4">
                    <div className={`w-10 h-10 rounded-full flex items-center justify-center ${colorClass}`}>
                      <Icon className="w-5 h-5" />
                    </div>
                    <div className="flex-1">
                      <h4 className="font-medium">{title}</h4>
                      <p className="text-sm text-muted-foreground mt-1 whitespace-pre-line line-clamp-4 break-words">{message}</p>
                      <p className="text-xs text-muted-foreground mt-2">
                        {new Date(notification.created_at).toLocaleString(i18n.language)}
                      </p>
                    </div>
                    {isUnread && (
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={(e) => { e.stopPropagation(); void markAsRead(notification.id); }}
                        className="text-farm-primary"
                      >
                        {t('notifications.markRead')}
                      </Button>
                    )}
                  </div>
                </CardContent>
              </Card>
            );
          })}
        </div>
      )}
    </div>
  );
}
